import { CDDRecord } from '../../types/cdd-records.types';
import { roleLabel } from './sections/shared/associatedPersonsConfig';
import {
  beneficialOwnerHeading,
  isBeneficialOwnerRole,
} from '../../services/cddRecordsApiConfig';
import {
  C,
  capitalize,
  fmtDate,
  getEntityName,
  getEntityRef,
  LINE_H,
  MB,
  ML, MR, MT,
  riskColor,
  SECTION_GAP,
} from './cddPdfExportConfig';

// ═══════════════════════════════════════════════════════════════════════════
//  PDF Builder — cursor-based, auto-paginates, clean document style
// ═══════════════════════════════════════════════════════════════════════════

class PDFBuilder {
  pdf: InstanceType<typeof import('jspdf')['jsPDF']>;  // type only;
  y: number;
  pageNum: number;
  W: number;
  H: number;
  contentW: number;
  refNum: string;
  entityType: string;
  totalPages = 0;

  constructor(refNum: string, entityType: string, jsPDF: any) {
    this.pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.W = this.pdf.internal.pageSize.getWidth();
    this.H = this.pdf.internal.pageSize.getHeight();
    this.contentW = this.W - ML - MR;
    this.y = MT;
    this.pageNum = 1;
    this.refNum = refNum;
    this.entityType = entityType;
  }

  // ─── Page management ───────────────────────────────────────────────

  private maxY(): number { return this.H - MB; }

  ensureSpace(needed: number): void {
    if (this.y + needed > this.maxY()) this.newPage();
  }

  newPage(): void {
    this.pdf.addPage();
    this.pageNum++;
    this.y = MT;
  }

  // ─── Watermarks + header/footer on every page ─────────────────────

  addWatermarks(): void {
    this.totalPages = this.pageNum;
    for (let i = 1; i <= this.totalPages; i++) {
      this.pdf.setPage(i);
      this.drawWatermark();
      this.drawHeaderFooter(i);
    }
  }

  private drawWatermark(): void {
    this.pdf.saveGraphicsState();
    const cx = this.W / 2;
    const cy = this.H / 2;

    // Giant diagonal "ARCaml"
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(100);
    this.pdf.setTextColor(...C.accent);
    // @ts-ignore
    this.pdf.setGState(new this.pdf.GState({ opacity: 0.07 }));
    this.pdf.text('ARCaml', cx, cy - 5, { align: 'center', angle: 30 });

    // "CDD VERIFIED SYSTEM"
    this.pdf.setFontSize(22);
    // @ts-ignore
    this.pdf.setGState(new this.pdf.GState({ opacity: 0.06 }));
    this.pdf.text('CDD VERIFIED SYSTEM', cx, cy + 18, { align: 'center', angle: 30 });

    // "CONFIDENTIAL" scattered
    this.pdf.setFontSize(14);
    // @ts-ignore
    this.pdf.setGState(new this.pdf.GState({ opacity: 0.05 }));
    const spots: [number, number][] = [
      [50, 45], [this.W - 50, 45],
      [50, this.H / 2], [this.W - 50, this.H / 2],
      [50, this.H - 40], [this.W - 50, this.H - 40],
    ];
    spots.forEach(([x, y]) => this.pdf.text('CONFIDENTIAL', x, y, { align: 'center', angle: -35 }));

    // Ref in corners
    this.pdf.setFont('courier', 'normal');
    this.pdf.setFontSize(7);
    // @ts-ignore
    this.pdf.setGState(new this.pdf.GState({ opacity: 0.08 }));
    this.pdf.text(this.refNum, ML, 10);
    this.pdf.text(this.refNum, this.W - MR, 10, { align: 'right' });

    this.pdf.restoreGraphicsState();
  }

  private drawHeaderFooter(pageNum: number): void {
    this.pdf.saveGraphicsState();
    // @ts-ignore
    this.pdf.setGState(new this.pdf.GState({ opacity: 1 }));

    // Top rule
    this.pdf.setDrawColor(...C.accent);
    this.pdf.setLineWidth(0.6);
    this.pdf.line(ML, 15, this.W - MR, 15);

    // Header left
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...C.accent);
    this.pdf.text('ARCaml', ML, 13);

    // Header right
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(...C.mid);
    this.pdf.text('CDD Record Report', this.W - MR, 13, { align: 'right' });

    // Bottom rule
    this.pdf.setDrawColor(...C.line);
    this.pdf.setLineWidth(0.3);
    const fy = this.H - 12;
    this.pdf.line(ML, fy, this.W - MR, fy);

    // Footer text
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(7);
    this.pdf.setTextColor(...C.light);
    this.pdf.text(`${this.refNum}  •  ${this.entityType}`, ML, fy + 4);
    this.pdf.text(`Page ${pageNum} of ${this.totalPages}`, this.W / 2, fy + 4, { align: 'center' });
    this.pdf.text(`Generated ${new Date().toLocaleDateString('en-AU')}`, this.W - MR, fy + 4, { align: 'right' });

    this.pdf.setFontSize(6);
    this.pdf.text('CONFIDENTIAL — Auto-generated by ARCaml CDD System', this.W / 2, fy + 7.5, { align: 'center' });

    this.pdf.restoreGraphicsState();
  }

  // ─── Text primitives ──────────────────────────────────────────────

  sectionTitle(title: string): void {
    this.ensureSpace(SECTION_GAP + 10);
    this.y += SECTION_GAP;

    // Background bar
    this.pdf.setFillColor(...C.bgLight);
    this.pdf.rect(ML, this.y - 4, this.contentW, 8, 'F');

    // Left accent stripe
    this.pdf.setFillColor(...C.accent);
    this.pdf.rect(ML, this.y - 4, 1.5, 8, 'F');

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(...C.black);
    this.pdf.text(title.toUpperCase(), ML + 5, this.y + 1);
    this.y += 10;
  }

  labelValue(lab: string, val: string, color?: [number, number, number]): void {
    this.ensureSpace(LINE_H);
    // Label
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(7.5);
    this.pdf.setTextColor(...C.mid);
    this.pdf.text(lab.toUpperCase(), ML, this.y);
    // Value
    this.pdf.setFont('helvetica', color ? 'bold' : 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...(color || C.dark));
    this.pdf.text(val || '—', ML + 45, this.y);
    this.y += LINE_H;
  }

  /**
   * A label with free-text underneath, wrapped to the page width (AL-438).
   *
   * labelValue() places the value on one line at a fixed x offset and never
   * wraps, which is fine for a date or a rating but silently runs a sentence off
   * the right edge of the page. Customer-declared prose needs wrapping, and in a
   * compliance document a truncated declaration is worse than an ugly one.
   *
   * The label sits on its own line here rather than beside the text, because a
   * wrapped block indented to ML+45 would leave very little usable width.
   */
  paragraph(lab: string, val: string): void {
    const text = (val || '').trim();
    if (!text) return;

    const maxWidth = this.pdf.internal.pageSize.getWidth() - ML - MR;

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(7.5);
    this.pdf.setTextColor(...C.mid);
    this.ensureSpace(LINE_H);
    this.pdf.text(lab.toUpperCase(), ML, this.y);
    this.y += LINE_H;

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...C.dark);

    // Split on the customer's own line breaks first, then wrap each to width, so
    // a multi-paragraph declaration keeps its structure.
    const lines: string[] = text
      .split(/\r?\n/)
      .flatMap((para) => (para.trim() ? (this.pdf.splitTextToSize(para, maxWidth) as string[]) : ['']));

    for (const line of lines) {
      // Checked per line so a long declaration breaks across pages rather than
      // overflowing the footer.
      this.ensureSpace(LINE_H);
      this.pdf.text(line, ML, this.y);
      this.y += LINE_H;
    }
  }

  twoCol(lab1: string, val1: string, lab2: string, val2: string): void {
    this.ensureSpace(LINE_H);
    const col2 = ML + this.contentW / 2;

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(7.5);
    this.pdf.setTextColor(...C.mid);
    this.pdf.text(lab1.toUpperCase(), ML, this.y);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...C.dark);
    this.pdf.text(val1 || '—', ML + 45, this.y);

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(7.5);
    this.pdf.setTextColor(...C.mid);
    this.pdf.text(lab2.toUpperCase(), col2, this.y);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(...C.dark);
    this.pdf.text(val2 || '—', col2 + 45, this.y);

    this.y += LINE_H;
  }

  separator(): void {
    this.pdf.setDrawColor(...C.line);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(ML, this.y, this.W - MR, this.y);
    this.y += 3;
  }

  gap(mm = 3): void { this.y += mm; }

  // ─── Stats row (simple text boxes, no gradients) ──────────────────

  statsRow(items: { label: string; value: string | number; color?: [number, number, number] }[]): void {
    this.ensureSpace(14);
    const colW = this.contentW / items.length;

    // Light bg
    this.pdf.setFillColor(248, 249, 252);
    this.pdf.rect(ML, this.y - 2, this.contentW, 11, 'F');
    this.pdf.setDrawColor(...C.line);
    this.pdf.setLineWidth(0.15);
    this.pdf.rect(ML, this.y - 2, this.contentW, 11, 'S');

    items.forEach((item, i) => {
      const x = ML + colW * i + colW / 2;
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(14);
      this.pdf.setTextColor(...(item.color || C.accent));
      this.pdf.text(String(item.value), x, this.y + 2, { align: 'center' });

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.5);
      this.pdf.setTextColor(...C.mid);
      this.pdf.text(item.label, x, this.y + 6.5, { align: 'center' });
    });

    this.y += 14;
  }

  // ─── Simple table ─────────────────────────────────────────────────

  table(headers: string[], rows: string[][], colWidths?: number[]): void {
    const widths = colWidths || headers.map(() => this.contentW / headers.length);
    const rowH = 6;

    // Header
    this.ensureSpace(rowH * 2);
    this.pdf.setFillColor(245, 245, 248);
    this.pdf.rect(ML, this.y - 3.5, this.contentW, rowH, 'F');
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(7);
    this.pdf.setTextColor(...C.mid);
    let xPos = ML + 2;
    headers.forEach((h, i) => {
      this.pdf.text(h.toUpperCase(), xPos, this.y);
      xPos += widths[i];
    });
    this.y += rowH;

    // Rows
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8);
    rows.forEach((row, ri) => {
      this.ensureSpace(rowH);
      if (ri % 2 === 0) {
        this.pdf.setFillColor(252, 252, 253);
        this.pdf.rect(ML, this.y - 3.5, this.contentW, rowH, 'F');
      }
      this.pdf.setTextColor(...C.dark);
      xPos = ML + 2;
      row.forEach((cell, i) => {
        this.pdf.text(cell, xPos, this.y);
        xPos += widths[i];
      });
      this.y += rowH;
    });

    this.pdf.setDrawColor(...C.line);
    this.pdf.setLineWidth(0.15);
    this.pdf.line(ML, this.y - 2, this.W - MR, this.y - 2);
    this.y += 2;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PDF EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export async function exportCDDRecordToPdf(record: CDDRecord): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const entityName = getEntityName(record);
  const entityRef = getEntityRef(record);
  const entityType = capitalize(record.entityType);

  const b = new PDFBuilder(record.referenceNumber, entityType, jsPDF);

  // ─── Title block ──────────────────────────────────────────────────

  b.y = 35;

  b.pdf.setFont('helvetica', 'bold');
  b.pdf.setFontSize(22);
  b.pdf.setTextColor(...C.black);
  b.pdf.text('CDD Record Report', ML, b.y);
  b.y += 10;

  b.pdf.setFont('helvetica', 'bold');
  b.pdf.setFontSize(16);
  b.pdf.setTextColor(...C.accent);
  b.pdf.text(entityName, ML, b.y);
  b.y += 8;

  b.pdf.setFont('helvetica', 'normal');
  b.pdf.setFontSize(9);
  b.pdf.setTextColor(...C.mid);
  b.pdf.text(`${record.referenceNumber}${entityRef ? '  •  ' + entityRef : ''}  •  ${entityType}`, ML, b.y);
  b.y += 4;

  b.separator();
  b.gap(2);

  // ─── Record Summary ───────────────────────────────────────────────

  b.sectionTitle('Record Summary');
  b.twoCol('Status', capitalize(record.status), 'Risk Level', record.riskLevel.toUpperCase());
  b.twoCol('Verification', capitalize(record.verificationStatus), 'Entity Type', entityType);
  b.twoCol('Created', fmtDate(record.createdDate), 'Last Modified', fmtDate(record.lastModifiedDate));
  if (record.nextReviewDate) b.labelValue('Next Review', fmtDate(record.nextReviewDate), C.accent);
  b.gap(3);

  b.statsRow([
    { label: 'Associated Persons', value: record.associatedPersons.length, color: C.accent },
    { label: 'Checks Completed', value: record.checksLog.checks.length, color: C.green },
    { label: 'Screening Results', value: record.screeningResults.length, color: C.amber },
  ]);

  // ─── Key Personnel ────────────────────────────────────────────────

  if (record.associatedPersons.length > 0) {
    const directors = record.associatedPersons.filter(p => p.roles.some(r => r === 'director' || r === 'trustee'));
    // AL-519 item 7: matches both ownership ids, so associations are not dropped.
    //
    // AL-529: the `|| r === 'shareholder'` clause that stood here is gone. It was
    // pulling company secretaries into the PDF's ownership section, because the
    // unmapped-ASIC fallback used to label them 'shareholder'. Nothing produces
    // that id now, so the clause was dead as well as wrong — and left in place it
    // would read as deliberate support for an id that no longer exists.
    const beneficialOwners = record.associatedPersons.filter(p =>
      p.roles.some(isBeneficialOwnerRole));

    if (directors.length > 0 || beneficialOwners.length > 0) {
      b.sectionTitle('Key Personnel');

      b.statsRow([
        { label: 'Verified Directors', value: directors.length, color: C.accent },
        // AL-519 item 7: the hedge is gone; named for this entity type.
        { label: beneficialOwnerHeading(record.entityType), value: beneficialOwners.length, color: C.mid },
      ]);

      if (directors.length > 0) {
        b.table(
          ['Name', 'Roles'],
          directors.map(p => [
            `${p.firstName} ${p.lastName}`,
            // AL-519 item 6: capitalize() on the id produced "Application-contact".
            p.roles.map(r => roleLabel(r)).join(', '),
          ]),
          [60, 110],
        );
      }

      if (beneficialOwners.length > 0) {
        b.table(
          ['Name', 'Ownership'],
          beneficialOwners.map(p => [
            `${p.firstName} ${p.lastName}`,
            p.ownershipPercentage !== undefined ? `${p.ownershipPercentage}%` : '—',
          ]),
          [100, 70],
        );
      }
    }
  }

  // ─── Associated Persons ───────────────────────────────────────────

  if (record.associatedPersons.length > 0) {
    b.sectionTitle('Associated Persons');

    const verified = record.associatedPersons.filter(p => p.verificationStatus === 'verified').length;
    const peps = record.associatedPersons.filter(p => p.isPEP).length;

    b.statsRow([
      { label: 'Total', value: record.associatedPersons.length },
      { label: 'Verified', value: verified, color: C.green },
      { label: 'PEP Flagged', value: peps, color: peps > 0 ? C.red : C.mid },
    ]);

    b.table(
      ['Name', 'Roles', 'Verification'],
      record.associatedPersons.map(p => [
        `${p.title ? p.title + ' ' : ''}${p.firstName} ${p.lastName}`,
        // AL-519 item 6: one vocabulary. This is a document the C1 keeps.
        p.roles.map(r => roleLabel(r)).join(', '),
        capitalize(p.verificationStatus),
      ]),
      [45, 100, 25],
    );
  }

  // ─── Screening Results ────────────────────────────────────────────

  if (record.screeningResults.length > 0) {
    b.sectionTitle('Screening Results');

    const clear = record.screeningResults.filter(r => r.status === 'clear').length;
    const potential = record.screeningResults.filter(r => r.status === 'potential-match').length;
    const confirmed = record.screeningResults.filter(r => r.status === 'confirmed-match').length;

    b.statsRow([
      { label: 'Clear', value: clear, color: C.green },
      { label: 'Potential Match', value: potential, color: C.amber },
      { label: 'Confirmed Match', value: confirmed, color: confirmed > 0 ? C.red : C.mid },
    ]);

    b.table(
      ['Type', 'Entity Screened', 'Date', 'Provider', 'Status', 'Matches'],
      record.screeningResults.map(r => [
        capitalize(r.screeningType),
        r.screenedEntity,
        fmtDate(r.screeningDate),
        (r.provider === 'SimpleKYC' || r.provider === 'IDVerse' ? 'ARCaml Engine' : r.provider) || '—',
        capitalize(r.status),
        `${r.matches?.length || 0}`,
      ]),
      [32, 38, 28, 28, 26, 18],
    );
  }

  // ─── Checks Log ───────────────────────────────────────────────────

  if (record.checksLog.checks.length > 0) {
    b.sectionTitle('Checks Log');

    const completed = record.checksLog.checks.filter(c => c.status === 'completed').length;
    const pending = record.checksLog.checks.filter(c => c.status === 'pending' || c.status === 'in-progress').length;
    const failed = record.checksLog.checks.filter(c => c.status === 'failed').length;

    b.statsRow([
      { label: 'Total', value: record.checksLog.checks.length },
      { label: 'Completed', value: completed, color: C.green },
      { label: 'Pending', value: pending, color: C.amber },
      { label: 'Failed', value: failed, color: failed > 0 ? C.red : C.mid },
    ]);

    b.table(
      ['Check Type', 'Date', 'Performed By', 'Status', 'Result', 'Details'],
      record.checksLog.checks.map(c => [
        capitalize(c.checkType),
        fmtDate(c.checkDate),
        c.performedBy,
        capitalize(c.status),
        c.result ? capitalize(c.result) : '—',
        c.details || '—',
      ]),
      [36, 26, 30, 24, 24, 30],
    );
  }

  // ─── Customer Assessment ──────────────────────────────────────────

  /**
   * AL-438: the heading and the nature-and-purpose declaration are emitted
   * independently of customerAssessment.
   *
   * The block below is gated on customerAssessment, which the transform only
   * builds when the report carries a riskAssessment. Putting the declaration
   * inside it would have dropped it from the PDF for exactly the cases with no
   * assessment yet — the same trap avoided on screen.
   */
  if (record.customerAssessment || record.natureOfBusiness) {
    b.sectionTitle('Customer Assessment');
  }

  if (record.natureOfBusiness) {
    b.paragraph('Nature & Purpose of Business Relationship', record.natureOfBusiness);
    b.gap(2);
  }

  if (record.customerAssessment) {
    const a = record.customerAssessment;

    if (a.customerCategory) b.labelValue('Category', a.customerCategory);
    if (a.customerSegment) b.labelValue('Segment', a.customerSegment);
    if (a.relationshipStartDate) b.labelValue('Relationship Since', fmtDate(a.relationshipStartDate));
    b.labelValue('Current Risk Rating', (a.currentRiskRating || 'N/A').toUpperCase(), riskColor(a.currentRiskRating || ''));

    if (a.sourceOfWealth) b.labelValue('Source of Wealth', a.sourceOfWealth);
    if (a.sourceOfFunds) b.labelValue('Source of Funds', a.sourceOfFunds);

    if (a.expectedActivity) {
      b.gap(2);
      b.pdf.setFont('helvetica', 'bold');
      b.pdf.setFontSize(8);
      b.pdf.setTextColor(...C.dark);
      b.pdf.text('Expected Activity', ML, b.y);
      b.y += LINE_H;
      if (a.expectedActivity.expectedVolume) b.labelValue('Volume', a.expectedActivity.expectedVolume);
      if (a.expectedActivity.expectedValue) b.labelValue('Value', a.expectedActivity.expectedValue);
      if (a.expectedActivity.transactionTypes?.length) b.labelValue('Transaction Types', a.expectedActivity.transactionTypes.join(', '));
      if (a.expectedActivity.countriesInvolved?.length) b.labelValue('Countries', a.expectedActivity.countriesInvolved.join(', '));
    }

    const latest = a.riskAssessments?.[0];
    if (latest?.customerRiskFactors?.length) {
      b.gap(3);
      b.table(
        ['Risk Factor', 'Rating', 'Notes'],
        latest.customerRiskFactors.map(f => [f.factor, capitalize(f.rating), f.notes || '—']),
        [55, 30, 85],
      );
    }
  }

  // ─── Finalize ─────────────────────────────────────────────────────

  b.addWatermarks();
  b.pdf.save(`CDD-${record.referenceNumber}-${new Date().toISOString().split('T')[0]}.pdf`);
}

// ─── CSV & JSON exporters ───────────────────────────────────────────────────

export function exportCDDRecordToCsv(record: CDDRecord): void {
  const rows: string[][] = [];
  rows.push(['CDD Record Export', '', '', '', '']);
  rows.push(['Reference', record.referenceNumber, 'Entity', getEntityName(record), record.entityType]);
  rows.push(['Status', record.status, 'Risk', record.riskLevel, `Verification: ${record.verificationStatus}`]);
  rows.push(['Created', fmtDate(record.createdDate), 'Modified', fmtDate(record.lastModifiedDate), '']);
  rows.push([]);

  rows.push(['ASSOCIATED PERSONS', '', '', '', '', '']);
  rows.push(['Name', 'Roles', 'Ownership %', 'Verification', 'Risk', 'PEP']);
  record.associatedPersons.forEach(p => {
    // AL-519 item 6: the CSV emitted bare ids ("application-contact"). The
    // ticket's renderer table missed this path; it is a fourth one.
    rows.push([`${p.firstName} ${p.lastName}`, p.roles.map(r => roleLabel(r)).join('; '), p.ownershipPercentage?.toString() || '', p.verificationStatus, p.riskLevel || '', p.isPEP ? 'Yes' : 'No']);
  });
  rows.push([]);

  rows.push(['SCREENING RESULTS', '', '', '', '']);
  rows.push(['Type', 'Entity', 'Date', 'Status', 'Matches']);
  record.screeningResults.forEach((r: any) => {
    rows.push([r.screeningType, r.screenedEntity, r.screeningDate, r.status, `${r.matches?.length || 0}`]);
  });
  rows.push([]);

  rows.push(['CHECKS LOG', '', '', '', '']);
  rows.push(['Type', 'Date', 'By', 'Status', 'Result']);
  record.checksLog.checks.forEach(c => {
    rows.push([c.checkType, c.checkDate, c.performedBy, c.status, c.result || '']);
  });

  const csv = rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CDD-${record.referenceNumber}-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * AL-519 item 6, deliberate exception: the JSON export keeps role IDS, not labels.
 *
 * The ticket groups JSON with the PDF and CSV, but they are not the same kind of
 * artefact. The PDF and CSV are read by a person, so they need the display
 * vocabulary. JSON is read by a machine, where the id is the stable contract and
 * the label is not — swapping in labels would break any consumer on the next copy
 * change, which is exactly the churn this ticket is causing.
 *
 * No change needed here: `record` is serialised whole, so roles are already the
 * mapped ids and they now reflect the corrected mapping.
 */
export function exportCDDRecordToJson(record: CDDRecord): void {
  const data = {
    exportDate: new Date().toISOString(),
    referenceNumber: record.referenceNumber,
    entityName: getEntityName(record),
    entityType: record.entityType,
    status: record.status,
    riskLevel: record.riskLevel,
    verificationStatus: record.verificationStatus,
    record,
  };
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CDD-${record.referenceNumber}-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
